import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Tema claro u oscuro del admin. La preferencia (claro, oscuro o la del sistema) se guarda en este navegador.
// Solo las pantallas ya rediseñadas cambian de tema; las demás siguen en oscuro hasta migrarlas (sus colores están fijos).
// Una entrada con '=' al inicio vale solo para esa ruta exacta (p. ej. la lista de Proyectos, no su formulario).
export const REDESIGNED = ['/agentes', '/herramientas', '/integraciones', '/login', '/dashboard', '/admin-equipo', '/admin-asistente', '=/proyects', '=/clients', '=/support'];
export const isRedesigned = (path) => REDESIGNED.some((p) => (p.startsWith('=') ? path === p.slice(1) : path === p || path.startsWith(`${p}/`)));

const KEY = 'admin-theme';
const read = () => { try { return localStorage.getItem(KEY) || 'system'; } catch { return 'system'; } };
const systemDark = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const ThemeCtx = createContext({ pref: 'system', resolved: 'dark', setPref: () => {}, themed: false });

/** Va dentro del Router: necesita la ruta para saber si la pantalla ya está rediseñada. */
export function ThemeProvider({ children }) {
  const { pathname } = useLocation();
  const [pref, setPrefState] = useState(read);
  const [sysDark, setSysDark] = useState(systemDark);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const on = (e) => setSysDark(e.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  const resolved = pref === 'system' ? (sysDark ? 'dark' : 'light') : pref;
  const themed = isRedesigned(pathname);
  const applied = themed ? resolved : 'dark';
  useEffect(() => {
    document.documentElement.dataset.theme = applied;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', applied === 'dark' ? '#0F0D17' : '#F6F5FB');
  }, [applied]);
  const value = useMemo(() => ({
    pref, resolved: applied, themed,
    setPref: (p) => { setPrefState(p); try { localStorage.setItem(KEY, p); } catch { /* sin almacenamiento */ } },
  }), [pref, applied, themed]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
